import { useCallback, useEffect, useState } from "react";
import {
    AlertColor,
    Box,
    Paper,
    Table,
    TableContainer,
    TablePagination,
    Tabs,
    Tab,
    Typography,
} from "@mui/material";
import { FormAlert, FormSnackbar } from "../../../../components/Shared/Form";
import { MediaView } from "../../../../components/Shared/MediaView";
import { useCampaignsDataStore } from "../../../../config/admin.stores";
import adminService from "../../../../services/admin.service";
import LoadingOverlay from "../../../../components/Shared/LoadingOverlay";
import Header from "../../../../components/Admin/Campaigns/Header";
import Rows from "../../../../components/Admin/Campaigns/Rows";

const AdminCampaigns = () => {
    const [tab, setTab] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [openMedia, setOpenMedia] = useState(false);
    const [mediaURLs, setMediaURLs] = useState<string[]>([]);
    const [mediaTitle, setMediaTitle] = useState("");

    const [alert, setAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("");
    const [alertId, setAlertId] = useState<number | null>(null);

    const [message, setMessage] = useState("");

    const [snackbar, setSnackbar] = useState("");
    const [severity, setSeverity] = useState<AlertColor>("success");

    const activeCampaigns = useCampaignsDataStore((state) => state.activeCampaigns);
    const setActiveCampaigns = useCampaignsDataStore((state) => state.setActiveCampaigns);
    const pendingApprovalCampaigns = useCampaignsDataStore((state) => state.pendingApprovalCampaigns);
    const setPendingApprovalCampaigns = useCampaignsDataStore((state) => state.setPendingApprovalCampaigns);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const activeResponse = await adminService.queryActive();
        const pendingResponse = await adminService.queryPending();
        setIsLoading(false);
        setActiveCampaigns(activeResponse);
        setPendingApprovalCampaigns(pendingResponse);
    }, [setActiveCampaigns, setPendingApprovalCampaigns])

    async function handleApproveAd(adId: number) {
        setIsLoading(true);
        const response = await adminService.approve(adId, message);
        if (response) {
            fetchData();
            setSeverity("success");
            setSnackbar("Ad has been approved.");
            setAlert(false);
            setMessage("");
        } else {
            setIsLoading(false);
            setSeverity("error");
            setSnackbar("There was an error approving the ad.");
        }
    }

    async function handleRejectAd(adId: number) {
        setIsLoading(true);
        const response = await adminService.reject(adId, message);
        if (response) {
            fetchData();
            setSeverity("success");
            setSnackbar("Ad has been rejected.");
            setAlert(false);
            setMessage("");
        } else {
            setIsLoading(false);
            setSeverity("error");
            setSnackbar("There was an error rejecting the ad.");
        }
    }

    const handleMediaChange = (urls: string[], title: string) => {
        setMediaURLs(urls);
        setMediaTitle(title);
        setOpenMedia(true);
    }

    const showAlert = (message: string, type: string, adId: number) => {
        setAlertMessage(message);
        setAlertType(type);
        setAlertId(adId);
        setAlert(true);
    }

    const formHandleAgree = () => {
        if (alertType && alertId) {
            return alertType === "approve" ? handleApproveAd(alertId) : handleRejectAd(alertId);
        }
    }
    
    useEffect(() => {
        if (activeCampaigns.length === 0 && pendingApprovalCampaigns.length === 0) { 
            fetchData();
        }
        // eslint-disable-next-line
    }, []);

    const handleTabChange = (_event: any, newValue: number) => {
        setTab(newValue);
        setPage(0);
    };

    function createTable(adStatus: string) {
        return (
            <>
                <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
                    <Table sx={{ minWidth: 1100 }}>
                        <Header adStatus={adStatus} />
                        <Rows
                            data={adStatus === "active" ? activeCampaigns : pendingApprovalCampaigns}
                            adStatus={adStatus}
                            page={page}
                            showAlert={showAlert}
                            handleMediaChange={handleMediaChange}
                        />
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={adStatus === "active" ? activeCampaigns.length : pendingApprovalCampaigns.length}
                    rowsPerPage={5}
                    rowsPerPageOptions={[]}
                    page={page}
                    onPageChange={(_event, newPage) => setPage(newPage)}
                />
            </>
        )
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'left',
                gap: 3,
                margin: 3,
            }}
        >
            <LoadingOverlay open={isLoading} />
            <Box display="flex" justifyContent="space-between">
                <Typography variant="h5">Manage Campaigns</Typography>
            </Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={handleTabChange}>
                    <Tab label="Active" id="active-tab" aria-controls="active-tabpanel"/>
                    <Tab label="Pending Approval" id="review-tab" aria-controls="review-tabpanel"/>
                </Tabs>
            </Box>
            <div role="tabpanel" hidden={tab !== 0} id="active-tabpanel" aria-labelledby="active-tab">
                {tab === 0 && createTable("active")}
            </div>
            <div role="tabpanel" hidden={tab !== 1} id="review-tabpanel" aria-labelledby="review-tab">
                {tab === 1 && createTable("pendingApproval")}
            </div>
            <MediaView title={mediaTitle} urls={mediaURLs} open={openMedia} setOpen={setOpenMedia} />
            <FormAlert
                label="admin-campaign"
                open={alert}
                setOpen={setAlert}
                description={alertMessage}
                disabled={isLoading}
                setText={alertType === "approve" ? undefined : setMessage}
                handleAgree={formHandleAgree}
                agreeText={alertType === "approve" ? "Approve Ad" : "Reject Ad"}
            />
            <FormSnackbar message={snackbar} setMessage={setSnackbar} severity={severity} />
        </Box>
    );
}

export default AdminCampaigns;
